import React, { useState } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { FaRobot, FaRocket, FaUsers, FaCheckCircle, FaHandshake } from 'react-icons/fa';

const points = [
  {
    icon: <FaRocket className="text-cyan-400 text-2xl" aria-label="Rocket icon representing Innovative Tech Stack" />,
    title: 'Innovative Tech Stack',
    desc: 'We use modern frameworks, AI, and ML to create cutting-edge applications and platforms.',
  },
  {
    icon: <FaUsers className="text-cyan-400 text-2xl" aria-label='Users icon representing Multidisciplinary Team' />,
    title: 'Multidisciplinary Team',
    desc: 'Our team combines creativity, engineering, and research to drive your vision to reality.',
  },
  {
    icon: <FaRobot className="text-cyan-400 text-2xl" aria-label='Robot icon representing AI-Driven Solutions'/>,
    title: 'AI-Driven Solutions',
    desc: 'We leverage neural networks and predictive modeling to automate and scale operations.',
  },
];

const whyChoose = [
  {
    icon: <FaHandshake className="text-purple-400" aria-label='Handshake icon representing Client-First Approach'/>,
    title: 'Client-First Approach',
    desc: 'We prioritize understanding your needs and delivering exactly what’s required — on time.',
  },
  {
    icon: <FaCheckCircle className="text-green-400" aria-label='Checkmark icon representing Quality and Reliability'/>,
    title: 'Quality & Reliability',
    desc: 'We follow strict QA protocols, ensuring your product is secure, robust, and scalable.',
  },
  {
    icon: <FaRocket className="text-pink-400" aria-label='Rocket icon representing Futuristic Vision' />,
    title: 'Futuristic Vision',
    desc: 'We aim for long-term relevance by constantly adapting to trends in AI, Web3, and automation.',
  },
];

const About = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-24 px-6 bg-[#0B0C1C] text-white" id="about">
      {/* Section Heading */}
      <motion.h2
        className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        About MASRIQ NextTech Solutions
      </motion.h2>

      <motion.p
        className="max-w-3xl mx-auto text-center text-white/80 text-lg mb-10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.8 }}
      >
        MASRIQ is a modern tech powerhouse revolutionizing AI, ML, Web, and Vision-based solutions.
        We help startups, enterprises, and communities with powerful digital products powered by
        intelligence, experience, and innovation.
      </motion.p>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto mb-12">
        {points.map((point, i) => (
          <motion.div
            key={i}
            className="bg-[#151730] p-6 rounded-xl text-center border border-white/10 hover:border-cyan-400 hover:shadow-[0_0_20px_rgba(34,211,238,0.3)] transition-all shadow-xl"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.2 }}
          >
            <div className="mb-3">{point.icon}</div>
            <h3 className="text-xl font-semibold mb-2">{point.title}</h3>
            <p className="text-white/70">{point.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Animated Counters */}
      <div className="flex flex-wrap justify-center gap-10 mb-12 text-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="text-4xl font-bold text-cyan-400">
            <CountUp end={1} duration={1} />+
          </h3>
          <p className="text-white/80 mt-1">Year of Experience</p>
        </motion.div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h3 className="text-4xl font-bold text-purple-400">
            <CountUp end={6} duration={3} />+
          </h3>
          <p className="text-white/80 mt-1">Projects Delivered</p>
        </motion.div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="text-4xl font-bold text-pink-400">
            <CountUp end={9} duration={3} />
          </h3>
          <p className="text-white/80 mt-1">Core Team Members</p>
        </motion.div>
      </div>

      {/* Why Choose Us */}
      <div className="max-w-4xl mx-auto mt-10 space-y-4">
        <h3 className="text-3xl font-semibold mb-4 text-center">Why Choose Us?</h3>
        {whyChoose.map((item, i) => (
          <motion.details
            key={i}
            open={openIndex === i}
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="bg-[#151730] p-4 rounded-lg border border-white/10 cursor-pointer group transition-all hover:shadow-[0_0_15px_rgba(34,211,238,0.3)]"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * i, duration: 0.6 }}
          >
            <summary className="flex items-center justify-between text-lg font-medium text-white/90 group-open:text-cyan-400">
              <div className="flex items-center gap-3">{item.icon}{item.title}</div>
              <motion.span
                animate={{ rotate: openIndex === i ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="text-white/40"
              >
                ▼
              </motion.span>
            </summary>
            <p className="text-white/70 mt-2">{item.desc}</p>
          </motion.details>
        ))}
      </div>
    </section>
  );
};

export default About;
